import { makeStyles } from 'tss-react/mui';
import { P2GDivider, P2GGrid, P2GProgress, P2GText } from '@parcel2go/core-ui';
import { IP2GBasketSection } from '~/hooks/use-basket-section';
import { AvailableExtra, Currency, QuoteExtras } from '~/lib/types/basket-types';
import Upsell from './upsell';
import ServiceUpgrade from '~/components/core/switcher/service-upgrade';
import P2GOptionContainer from '~/components/core/option/option';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { useTaxName } from '@parcel2go/npm-p2g-app';

type UpsellsProps = {
  section: IP2GBasketSection<QuoteExtras>;
  currency: Currency;
  extras: AvailableExtra[];
  onChange: (extra: string, selected: boolean) => void;
  includeServiceUpgrade?: boolean;
  quoteIsDropOff: boolean;
};

// Needs moving to css file.
const useStyles = makeStyles()((theme) => ({
  root: {
    '& > hr': {
      display: 'block !important',
      margin: theme.spacing(1, 0) + ' !important',
      borderColor: theme.palette.grey[200] + ' !important'
    }
  }
}));

export default function Upsells({
  section,
  currency,
  extras,
  onChange,
  includeServiceUpgrade,
  quoteIsDropOff
}: UpsellsProps) {
  const { loading, data } = section;
  const taxName = useTaxName();
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const { classes } = useStyles();
  const extrasHaveTax = extras?.some((extra) => (extra.price?.tax || 0) > 0);

  if (loading) return <P2GProgress />;

  return (
    <P2GGrid container direction="column" spacing={4} dataTestId="extras-options" wrap="nowrap">
      {extras.length > 0 && (
        <P2GGrid item>
          <P2GOptionContainer variant="extra" selected={false}>
            <P2GGrid container direction="column" gap={extras.length == 1 ? 1 : 2}>
              {extras.map((extra, i) => (
                <React.Fragment key={extra.key}>
                  {i > 0 && (
                    <P2GGrid item xs={12} classNames={classes.root}>
                      <P2GDivider variant="fullWidth" />
                    </P2GGrid>
                  )}
                  <P2GGrid item xs={12}>
                    <Upsell
                      quoteIsDropOff={quoteIsDropOff}
                      extra={extra}
                      currency={currency}
                      selected={data.extras.some((e) => e.key === extra.key)}
                      onChange={onChange}
                      loading={loading}
                    />
                  </P2GGrid>
                </React.Fragment>
              ))}
              {extrasHaveTax && (
                <P2GGrid item xs={12}>
                  <P2GText colour="primary" variant="caption">
                    {t(`PRICE_EXC_TAX`, {
                      defaultValue: `* Prices exclude {{taxName}}`,
                      taxName: taxName
                    })}
                  </P2GText>
                </P2GGrid>
              )}
            </P2GGrid>
          </P2GOptionContainer>
        </P2GGrid>
      )}
      {includeServiceUpgrade && <ServiceUpgrade />}
    </P2GGrid>
  );
}
